import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";

import i18n from "../../config/i18n";
// Internal imports (ordered: services/cache/utils)
// Migrating from legacy api adapter to sharedApi (@lingoshade/api)
import { sharedApi, getAuthToken } from "../../services/sharedApi";
import { speechService } from "../../services/speechService";
import { voiceService } from "../../services/voiceService";
import { fetchWithCache } from "../../utils/cache/cache";
import { toApiError } from "../../utils/errorUtils";
import { logger } from "../../utils/logger";

// Use Text from shared package types (via texts endpoint) & define minimal ProgressEntry used in slice until backend migration completes
import type { VoiceWithTier } from "../../services/voiceService";
import type { Text as SharedText } from "@lingoshade/api";

export interface ProgressEntry {
  id: string;
  textId: string;
  userId: string;
  timeSpent: number;
  completed: boolean;
  completedAt?: string;
  score?: number;
  createdAt: string;
  updatedAt: string;
}

type Text = SharedText; // alias for local usage

// Расширенный PracticeState для полной функциональности
export interface PracticeState {
  // Original progress data
  currentText: Text | null;
  progress: ProgressEntry[];
  stats: {
    totalTimeSpent: number;
    totalTextsCompleted: number;
    averageScore: number;
  };
  loading: boolean;
  saving: boolean;
  error: string | null;

  // Extended audio state
  audio: {
    loading: boolean;
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    playbackSpeed: number;
    audioUrl: string | null;
    currentVoiceId: string | null;
    wordBoundaries: Array<{
      word: string;
      offset: number;
      duration: number;
    }>;
    currentWordIndex: number;
    // Loop playback over selected word range (A–B)
    loopMode: {
      enabled: boolean;
      startWordIndex: number | null;
      endWordIndex: number | null;
      repeatCount: number; // for analytics/UI only
      pauseMs: number; // delay between loop repeats
    };
  };

  // Voice settings
  voice: {
    selectedVoice: string;
    availableVoices: VoiceWithTier[];
  };

  // UI state
  ui: {
    showSettings: boolean;
    showSubscriptionModal: boolean;
    showTranslation: boolean;
    translation: string;
    translationLoading: boolean;
    // When true, single clicks on words set A/B points instead of translation
    loopSelectionMode: boolean;
  };

  // Practice session
  session: {
    startTime: Date | null;
    sessionsCompleted: number;
    sessionProgress: number;
    isCompleted: boolean;
    sessionTime: number;
  };

  // Rating and favorites
  rating: {
    userRating: number;
    isFavorited: boolean;
    ratingSubmitted: boolean;
    ratingLoading: boolean;
  };
}

const initialState: PracticeState = {
  currentText: null,
  progress: [],
  stats: {
    totalTimeSpent: 0,
    totalTextsCompleted: 0,
    averageScore: 0,
  },
  loading: false,
  saving: false,
  error: null,

  audio: {
    loading: false,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    playbackSpeed: 1,
    audioUrl: null,
    currentVoiceId: null,
    wordBoundaries: [],
    currentWordIndex: -1,
    loopMode: {
      enabled: false,
      startWordIndex: null,
      endWordIndex: null,
      repeatCount: 0,
      pauseMs: 1000,
    },
  },

  voice: {
    selectedVoice: "",
    availableVoices: [],
  },

  ui: {
    showSettings: false,
    showSubscriptionModal: false,
    showTranslation: false,
    translation: "",
    translationLoading: false,
    loopSelectionMode: false,
  },

  session: {
    startTime: null,
    sessionsCompleted: 0,
    sessionProgress: 0,
    isCompleted: false,
    sessionTime: 0,
  },

  rating: {
    userRating: 0,
    isFavorited: false,
    ratingSubmitted: false,
    ratingLoading: false,
  },
};

// Fetch text by ID
export const fetchText = createAsyncThunk<
  Text,
  string,
  { rejectValue: string }
>("practice/fetchText", async (textId, { rejectWithValue }) => {
  try {
    return await fetchWithCache<Text>({
      key: `text:${textId}`,
      ttl: 15 * 60_000,
      swr: 5 * 60_000,
      tags: ["texts", `text:${textId}`],
      request: async () => {
        const token = getAuthToken();
        const response = await sharedApi.texts.getById(textId, token);
        // shared endpoint may return either { text, access } or direct Text
        interface TextResponse {
          text?: Text;
        }
        const typedResponse = response as Text | TextResponse;
        if (
          typeof typedResponse === "object" &&
          typedResponse !== null &&
          "text" in typedResponse
        ) {
          return typedResponse.text as Text;
        }
        return typedResponse as Text;
      },
    });
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(
      apiError.message || i18n.t("practice:errors.failedToFetchText"),
    );
  }
});

// Fetch user progress (оставляем, можно затем тоже кэшировать)
export const fetchProgress = createAsyncThunk<
  { progress: ProgressEntry[]; stats: PracticeState["stats"] },
  void,
  { rejectValue: string }
>("practice/fetchProgress", async (_, { rejectWithValue }) => {
  try {
    return await fetchWithCache({
      key: "progress:user",
      ttl: 2 * 60_000,
      swr: 60_000,
      tags: ["progress"],
      request: async () => {
        // TODO: Replace with dedicated progress endpoint when exposed in shared API.
        // For now fallback to feature tracking statistics not available -> return empty progress.
        return {
          progress: [],
          stats: { totalTimeSpent: 0, totalTextsCompleted: 0, averageScore: 0 },
        };
      },
    });
  } catch (error: unknown) {
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("practice:errors.failedToFetchProgress");
    return rejectWithValue(message);
  }
});

// Record progress
export const recordProgress = createAsyncThunk<
  void,
  { textId: string; timeSpent: number; completed?: boolean; score?: number },
  { rejectValue: string }
>("practice/recordProgress", async (data, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    await sharedApi.features.trackProgress(
      {
        textId: data.textId,
        completedAt: data.completed ? new Date().toISOString() : undefined,
        performanceData: {
          playCount: 1,
          totalDuration: data.timeSpent,
          pauseCount: 0,
          // Use score to influence speed heuristically if present; fallback 1
          rewoundCount: 0,
          speed: 1,
        },
      },
      token,
    );
  } catch (error: unknown) {
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("practice:errors.failedToRecordProgress");
    return rejectWithValue(message);
  }
});

// Mark text as completed
export const markCompleted = createAsyncThunk<
  void,
  { textId: string; timeSpent: number; score?: number },
  { rejectValue: string }
>("practice/markCompleted", async (data, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    await sharedApi.features.trackProgress(
      {
        textId: data.textId,
        completedAt: new Date().toISOString(),
        performanceData: {
          playCount: 1,
          totalDuration: data.timeSpent,
          pauseCount: 0,
          rewoundCount: 0,
          speed: 1,
        },
      },
      token,
    );
  } catch (error: unknown) {
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("practice:errors.failedToMarkAsCompleted");
    return rejectWithValue(message);
  }
});

// Voices with cache (по языку)
export const fetchVoices = createAsyncThunk<
  VoiceWithTier[],
  string,
  { rejectValue: string }
>("practice/fetchVoices", async (language, { rejectWithValue }) => {
  logger.debug("=== fetchVoices thunk ENTERED ===", { language });
  try {
    logger.debug("=== fetchVoices thunk TRY block ===");
    logger.debug("[practice/fetchVoices] fetching voices for language", {
      language,
    });
    logger.debug("=== fetchVoices thunk START ===", { language });

    // TEMPORARY: Direct API call without cache to debug
    logger.debug("=== About to call voiceService ===");
    logger.debug(
      "[practice/fetchVoices] calling voiceService.getVoicesForLanguage DIRECTLY",
      { language },
    );
    const response = await voiceService.getVoicesForLanguage(language);
    logger.debug("=== fetchVoices API response ===", response);
    logger.debug("[practice/fetchVoices] voiceService response", {
      voicesCount: response.voices?.length || 0,
      voices: response.voices?.slice(0, 3).map((v) => v.voiceId) || [],
    });
    logger.debug("=== Returning voices ===", response.voices);
    return response.voices;

    /* ORIGINAL CODE WITH CACHE - temporarily disabled
    const result = await fetchWithCache<VoiceWithTier[]>({
      key: `voices:language:${language}`,
      ttl: 10 * 60_000,
      swr: 5 * 60_000,
      tags: ["voices", `voices:${language}`],
      request: async () => {
        logger.debug("[practice/fetchVoices] calling voiceService.getVoicesForLanguage", { language });
        const response = await voiceService.getVoicesForLanguage(language);
        logger.debug("[practice/fetchVoices] voiceService response", { 
          voicesCount: response.voices?.length || 0,
          voices: response.voices?.slice(0, 3).map(v => v.voiceId) || []
        });
        return response.voices;
      },
    });
    logger.debug("[practice/fetchVoices] cache result", { 
      voicesCount: result?.length || 0 
    });
    return result;
    */
  } catch (error) {
    logger.error("=== fetchVoices thunk ERROR ===", error);
    logger.error("[practice/fetchVoices] error", error);
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("practice:errors.failedToFetchVoices");
    return rejectWithValue(message);
  }
});

export const generateAudio = createAsyncThunk<
  {
    audioUrl: string;
    wordBoundaries: Array<{ word: string; offset: number; duration: number }>;
    voiceId: string;
  },
  { textId: string; voiceId: string },
  { rejectValue: string }
>(
  "practice/generateAudio",
  async ({ textId, voiceId }, { rejectWithValue }) => {
    try {
      logger.debug("[practice/generateAudio] generating audio", {
        textId,
        voiceId,
      });

      // First check if user has access to this voice
      const accessCheck = await voiceService.checkVoiceAccess(voiceId);

      if (!accessCheck.canAccess) {
        logger.error("[practice/generateAudio] voice access denied", {
          reason: accessCheck.reason,
        });
        return rejectWithValue(
          accessCheck.reason || i18n.t("practice:errors.voiceAccessDenied"),
        );
      }
      logger.debug(
        "[practice/generateAudio] voice access granted, synthesizing speech",
      );
      const response = await speechService.synthesizeSpeech({
        textId,
        voiceId,
      });

      const audioUrl =
        response.audioUrl || speechService.createAudioUrl(response.audio);
      logger.debug("[practice/generateAudio] audio generated", {
        audioUrl: audioUrl.substring(0, 50) + "...",
        hasWordBoundaries: !!response.wordBoundaries,
        wordBoundariesCount: response.wordBoundaries?.length || 0,
      });

      return {
        audioUrl,
        wordBoundaries: response.wordBoundaries || [],
        voiceId,
      };
    } catch (error: unknown) {
      logger.error("[practice/generateAudio] error", error);
      const message = toApiError(error).message;
      return rejectWithValue(message);
    }
  },
);

export const translateText = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("practice/translateText", async (textId, { rejectWithValue }) => {
  try {
    return await fetchWithCache<string>({
      key: `translation:${textId}`,
      ttl: 24 * 60 * 60_000,
      swr: 0,
      tags: ["translations", `translation:${textId}`],
      request: async () => {
        const token = getAuthToken();
        const response = await sharedApi.texts.getUserTranslation(
          textId,
          token,
        );
        return response.translation;
      },
    });
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("practice:errors.failedToTranslateText");
    return rejectWithValue(message);
  }
});

export const submitRating = createAsyncThunk<
  { textId: string; rating: number; averageRating: number },
  { textId: string; rating: number },
  { rejectValue: string }
>("practice/submitRating", async ({ textId, rating }, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    const response = await sharedApi.texts.rate(textId, rating, token);
    return { textId, rating, averageRating: response.averageRating };
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("practice:errors.failedToSubmitRating");
    return rejectWithValue(message);
  }
});

export const toggleFavorite = createAsyncThunk<
  { textId: string; favorited: boolean },
  string,
  { rejectValue: string }
>("practice/toggleFavorite", async (textId, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    const response = await sharedApi.texts.toggleFavorite(textId, token);
    return { textId, favorited: response.favorited };
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("practice:errors.failedToToggleFavorite");
    return rejectWithValue(message);
  }
});

const practiceSlice = createSlice({
  name: "practice",
  initialState,
  reducers: {
    setCurrentText: (state, action: PayloadAction<Text | null>): void => {
      state.currentText = action.payload;
    },
    clearError: (state): void => {
      state.error = null;
    },
    clearCurrentText: (state): void => {
      state.currentText = null;
    },

    // Audio controls
    setAudioPlaying: (state, action: PayloadAction<boolean>): void => {
      state.audio.isPlaying = action.payload;
    },
    setAudioCurrentTime: (state, action: PayloadAction<number>): void => {
      state.audio.currentTime = action.payload;
    },
    setAudioDuration: (state, action: PayloadAction<number>): void => {
      state.audio.duration = action.payload;
    },
    setAudioVolume: (state, action: PayloadAction<number>): void => {
      state.audio.volume = action.payload;
    },
    setPlaybackSpeed: (state, action: PayloadAction<number>): void => {
      state.audio.playbackSpeed = action.payload;
    },
    setCurrentWordIndex: (state, action: PayloadAction<number>): void => {
      state.audio.currentWordIndex = action.payload;
    },
    // Loop selection & playback controls (A–B)
    setLoopSelectionMode: (state, action: PayloadAction<boolean>): void => {
      state.ui.loopSelectionMode = action.payload;
      // When turning off selection mode, do not reset actual loop playback
    },
    setLoopStartWord: (state, action: PayloadAction<number>): void => {
      state.audio.loopMode.startWordIndex = action.payload;
      // Reset end until user selects it explicitly
      state.audio.loopMode.endWordIndex = null;
    },
    setLoopEndWord: (state, action: PayloadAction<number>): void => {
      const start = state.audio.loopMode.startWordIndex;
      let end = action.payload;
      if (typeof start === "number" && start >= 0 && end < start) {
        // Normalize A/B order
        state.audio.loopMode.startWordIndex = end;
        end = start;
      }
      state.audio.loopMode.endWordIndex = end;
    },
    setLoopPauseMs: (state, action: PayloadAction<number>): void => {
      const val = Math.max(0, Math.min(5000, action.payload));
      state.audio.loopMode.pauseMs = val;
    },
    enableLoopPlayback: (state): void => {
      // Enable only if both boundaries are defined and valid
      const { startWordIndex, endWordIndex } = state.audio.loopMode;
      if (
        typeof startWordIndex === "number" &&
        typeof endWordIndex === "number" &&
        startWordIndex >= 0 &&
        endWordIndex >= 0 &&
        startWordIndex < endWordIndex
      ) {
        state.audio.loopMode.enabled = true;
      }
    },
    // Включить режим петли (готовность к выбору границ)
    enableLoopMode: (state): void => {
      state.audio.loopMode.enabled = true;
    },
    clearLoopPlayback: (state): void => {
      state.audio.loopMode.enabled = false;
      state.audio.loopMode.startWordIndex = null;
      state.audio.loopMode.endWordIndex = null;
      state.audio.loopMode.repeatCount = 0;
      state.audio.loopMode.pauseMs = 1000;
    },
    incrementLoopRepeatCount: (state): void => {
      state.audio.loopMode.repeatCount += 1;
    },
    // Полная очистка текущего аудио источника (при смене голоса/текста)
    clearAudioSource: (state): void => {
      state.audio.audioUrl = null;
      state.audio.currentVoiceId = null;
      state.audio.wordBoundaries = [];
      state.audio.currentWordIndex = -1;
      state.audio.currentTime = 0;
      state.audio.duration = 0;
      state.audio.isPlaying = false;
      state.audio.loopMode = {
        enabled: false,
        startWordIndex: null,
        endWordIndex: null,
        repeatCount: 0,
        pauseMs: 1000,
      };
    },

    // Voice settings
    setSelectedVoice: (state, action: PayloadAction<string>): void => {
      state.voice.selectedVoice = action.payload;
    },

    // UI state
    setShowSettings: (state, action: PayloadAction<boolean>): void => {
      state.ui.showSettings = action.payload;
    },
    setShowSubscriptionModal: (state, action: PayloadAction<boolean>): void => {
      state.ui.showSubscriptionModal = action.payload;
    },
    setShowTranslation: (state, action: PayloadAction<boolean>): void => {
      state.ui.showTranslation = action.payload;
    },

    // Session state
    startSession: (state): void => {
      state.session.startTime = new Date();
      state.session.sessionProgress = 0;
      state.session.isCompleted = false;
    },
    completeSession: (state): void => {
      state.session.isCompleted = true;
      state.session.sessionsCompleted += 1;
      state.session.sessionProgress = 100;
    },
    updateSessionProgress: (state, action: PayloadAction<number>): void => {
      state.session.sessionProgress = action.payload;
    },
    updateSessionTime: (state, action: PayloadAction<number>): void => {
      state.session.sessionTime = action.payload;
    },

    // Rating state
    setUserRating: (state, action: PayloadAction<number>): void => {
      state.rating.userRating = action.payload;
    },

    // Reset state
    resetPractice: (state): void => {
      state.currentText = null;
      state.audio = initialState.audio;
      state.session = initialState.session;
      state.ui = { ...initialState.ui };
      state.rating = initialState.rating;
    },
    resetPracticeState: () => initialState,
  },
  extraReducers: (builder): void => {
    // Fetch text
    builder
      .addCase(fetchText.pending, (state): void => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchText.fulfilled, (state, action): void => {
        state.loading = false;
        state.currentText = action.payload;
        // Инициализация локального состояния рейтинга/избранного из текста
        // Расширяем текст безопасно, не полагаясь на any
        const t = action.payload as Text & {
          userRating?: number;
          isFavorited?: boolean;
        };
        state.rating.userRating =
          typeof t.userRating === "number" ? t.userRating : 0;
        state.rating.isFavorited = !!t.isFavorited;
      })
      .addCase(fetchText.rejected, (state, action): void => {
        state.loading = false;
        state.error =
          action.payload || i18n.t("practice:errors.failedToFetchText");
      });

    // Fetch progress
    builder
      .addCase(fetchProgress.pending, (state): void => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProgress.fulfilled, (state, action): void => {
        state.loading = false;
        state.progress = action.payload.progress;
        state.stats = action.payload.stats;
      })
      .addCase(fetchProgress.rejected, (state, action): void => {
        state.loading = false;
        state.error =
          action.payload || i18n.t("practice:errors.failedToFetchProgress");
      });

    // Record progress
    builder
      .addCase(recordProgress.pending, (state): void => {
        state.saving = true;
        state.error = null;
      })
      .addCase(recordProgress.fulfilled, (state): void => {
        state.saving = false;
      })
      .addCase(recordProgress.rejected, (state, action): void => {
        state.saving = false;
        state.error =
          action.payload || i18n.t("practice:errors.failedToRecordProgress");
      });

    // Mark completed
    builder
      .addCase(markCompleted.pending, (state): void => {
        state.saving = true;
        state.error = null;
      })
      .addCase(markCompleted.fulfilled, (state): void => {
        state.saving = false;
        // Optionally update stats or progress here
      })
      .addCase(markCompleted.rejected, (state, action): void => {
        state.saving = false;
        state.error =
          action.payload || i18n.t("practice:errors.failedToMarkAsCompleted");
      });

    // Fetch voices
    builder
      .addCase(fetchVoices.pending, (_state): void => {
        logger.debug("[practice/reducer] fetchVoices.pending");
      })
      .addCase(fetchVoices.fulfilled, (state, action): void => {
        logger.debug("=== fetchVoices.fulfilled REDUCER ===", action.payload);
        logger.debug("[practice/reducer] fetchVoices.fulfilled", {
          voicesCount: action.payload.length,
          voices: action.payload.map((v) => ({ id: v.voiceId, tier: v.tier })),
        });
        state.voice.availableVoices = action.payload;
        logger.debug(
          "=== state.voice.availableVoices UPDATED ===",
          state.voice.availableVoices,
        );

        // Check if current selected voice is available in new language
        const currentVoiceStillValid =
          state.voice.selectedVoice &&
          action.payload.some((v) => v.voiceId === state.voice.selectedVoice);

        if (action.payload.length > 0 && !currentVoiceStillValid) {
          // Select first BASIC voice if available, otherwise first voice
          const basicVoices = action.payload.filter((v) => v.tier === "BASIC");
          const defaultVoice =
            basicVoices.length > 0 ? basicVoices[0] : action.payload[0];
          state.voice.selectedVoice = defaultVoice.voiceId;
          logger.debug("=== AUTO-SELECTED VOICE ===", defaultVoice.voiceId);
          logger.debug("[practice/reducer] auto-selected voice", {
            voiceId: defaultVoice.voiceId,
            tier: defaultVoice.tier,
            reason: state.voice.selectedVoice
              ? "previous voice not available"
              : "no voice selected",
          });
        } else {
          logger.debug("=== NO AUTO-SELECT ===", {
            payloadLength: action.payload.length,
            currentSelected: state.voice.selectedVoice,
            currentVoiceStillValid,
          });
        }
      })
      .addCase(fetchVoices.rejected, (_state, action): void => {
        logger.error("[practice/reducer] fetchVoices.rejected", {
          error: action.payload,
        });
      });

    // Generate audio
    builder
      .addCase(generateAudio.pending, (state): void => {
        state.audio.loading = true;
        logger.debug("[practice/reducer] generateAudio.pending");
      })
      .addCase(generateAudio.fulfilled, (state, action): void => {
        state.audio.loading = false;
        state.audio.audioUrl = action.payload.audioUrl;
        state.audio.wordBoundaries = action.payload.wordBoundaries;
        state.audio.currentVoiceId = action.payload.voiceId;
        state.audio.currentWordIndex = -1; // Reset current word
        logger.debug("[practice/reducer] generateAudio.fulfilled", {
          audioUrl: action.payload.audioUrl?.substring(0, 50) + "...",
          wordBoundariesCount: action.payload.wordBoundaries.length,
          voiceId: action.payload.voiceId,
        });
      })
      .addCase(generateAudio.rejected, (state, action): void => {
        state.audio.loading = false;
        state.error =
          action.payload || i18n.t("practice:errors.failedToGenerateAudio");
        logger.error("[practice/reducer] generateAudio.rejected", {
          error: action.payload,
        });
      });

    // Translate text
    builder
      .addCase(translateText.pending, (state): void => {
        state.ui.translationLoading = true;
      })
      .addCase(translateText.fulfilled, (state, action): void => {
        state.ui.translationLoading = false;
        state.ui.translation = action.payload;
        state.ui.showTranslation = true;
      })
      .addCase(translateText.rejected, (state, action): void => {
        state.ui.translationLoading = false;
        state.error =
          action.payload || i18n.t("practice:errors.failedToTranslateText");
      });

    // Submit rating
    builder
      .addCase(submitRating.pending, (state): void => {
        state.rating.ratingLoading = true;
      })
      .addCase(submitRating.fulfilled, (state, action): void => {
        state.rating.ratingLoading = false;
        state.rating.ratingSubmitted = true;
        state.rating.userRating = action.payload.rating;
        if (
          state.currentText &&
          state.currentText.id === action.payload.textId
        ) {
          state.currentText.userRating = action.payload.rating;
          state.currentText.averageRating = action.payload.averageRating;
        }
      })
      .addCase(submitRating.rejected, (state, action): void => {
        state.rating.ratingLoading = false;
        state.error =
          action.payload || i18n.t("practice:errors.failedToSubmitRating");
      });

    // Toggle favorite
    builder.addCase(toggleFavorite.fulfilled, (state, action): void => {
      state.rating.isFavorited = action.payload.favorited;
      if (state.currentText && state.currentText.id === action.payload.textId) {
        (state.currentText as Text & { isFavorited?: boolean }).isFavorited =
          action.payload.favorited;
      }
      // NOTE: Для синхронизации списка текстов (textsSlice) добавлен reducer applyFavoriteStatus.
      // Здесь можно задействовать listenerMiddleware (не реализовано пока) чтобы диспатчить applyFavoriteStatus.
    });
  },
});

export const {
  setCurrentText,
  clearError,
  clearCurrentText,
  // Audio controls
  setAudioPlaying,
  setAudioCurrentTime,
  setAudioDuration,
  setAudioVolume,
  setPlaybackSpeed,
  setCurrentWordIndex,
  // Loop selection & playback
  setLoopSelectionMode,
  setLoopStartWord,
  setLoopEndWord,
  enableLoopPlayback,
  enableLoopMode,
  clearLoopPlayback,
  incrementLoopRepeatCount,
  setLoopPauseMs,
  // Audio source management
  clearAudioSource,
  // Voice settings
  setSelectedVoice,
  // UI state
  setShowSettings,
  setShowSubscriptionModal,
  setShowTranslation,
  // Session state
  startSession,
  completeSession,
  updateSessionProgress,
  updateSessionTime,
  // Rating state
  setUserRating,
  // Reset state
  resetPractice,
  resetPracticeState,
} = practiceSlice.actions;
export default practiceSlice.reducer;
